import { mapObject } from 'utils/cms/utils';
import { mergeInto, OVERRIDE } from 'utils/dynamicData';
import { getSeoFooterMessaging } from 'utils/seoFooterMessaging';

const currentYear = new Date().getFullYear();
// Hub site page IDs that can be selected as internal link targets in Nav & Legal.
// These need to be converted to absolute paths so they don't resolve as broken relative URLs on movie pages.
const HUB_PAGE_IDS = new Set(['landing', 'about', 'news', 'releases', 'leadership', 'contact', 'terms-of-use']);
// CMS destinations that map onto a Bolt page key, so the footer resolves them as internal navigation.
const PAGE_KEYS_BY_DESTINATION = {
	tickets: 'boltShowtimes',
	showtimes: 'boltShowtimes',
	homeEnt: 'boltHomeEnt',
	gallery: 'boltGallery',
	videos: 'boltVideos',
	synopsis: 'boltSynopsis',
};

function resolveDestination(destination) {
	if (!destination) return null;
	if (destination === 'cookies') return 'cookies';
	// Already a full URL
	if (/^(https?:)?\/\//.test(destination)) return destination;
	const slug = destination.replace(/^\/(?!\/)/, '').replace(/\/$/, '');
	// TODO: We should not rely on the slug (could be localized for different regions)
	//   hub page ids should be added as external links
	if (HUB_PAGE_IDS.has(slug)) return `/${slug}/`;
	return PAGE_KEYS_BY_DESTINATION[slug] || destination;
}

// The footer components sort by `order` and address entries by key, so arrays are keyed here.
function toOrderedObject(items) {
	if (!items || typeof items !== 'object') return {};
	if (!Array.isArray(items)) return items;
	return Object.fromEntries(items.map((item, i) => [crypto.randomUUID(), { ...item, order: i }]));
}

function mapLinks(cmsData) {
	const globalLinks = toOrderedObject(cmsData?.footer?._navigationFooter?.moviePageFooter?.links);
	const localeLinks = toOrderedObject(cmsData?.footer?.links);
	const mergedLinks = mergeInto(globalLinks, localeLinks);

	const links = mapObject(mergedLinks, (item) => ({
		$copy: item.name,
		order: item.order,
		url: resolveDestination(item.destination),
	}));

	return {
		...links,
		[OVERRIDE]: true
	};
}

function mapLogos(cmsData) {
	const logos = mapObject(cmsData?.footer?.logos, (item) => ({
		alt: item.alt || item.name,
		order: item.order,
		src: item.url,
	}));

	return {
		...logos,
		[OVERRIDE]: true
	};
}

function mapCopyright(cmsData, appData) {
	const globalCopyright = toOrderedObject(cmsData?.footer?._navigationFooter?.moviePageFooter?.copyright);
	const localeCopyright = toOrderedObject(cmsData?.footer?.copyright);
	const mergedCopyright = mergeInto(globalCopyright, localeCopyright);

	const copyright = mapObject(mergedCopyright, (item) => ({
		$copy: item.name.replace(/\[YEAR\]/gi, currentYear),
		order: item.order,
		url: resolveDestination(item.destination),
	}));

	return {
		...copyright,
		seoMessaging: {
			$copy: cmsData?.footer?.seoMessaging || getSeoFooterMessaging(cmsData, appData?.copy?.baseCopy),
			order: 999,
			url: null,
		},
		[OVERRIDE]: true,
	};
}

export default function mapFooter(cmsData, appData) {
	return {
		defaultFooter: {
			copyright: mapCopyright(cmsData, appData),
			links: mapLinks(cmsData),
			logos: mapLogos(cmsData),
		}
	};
}